import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Check, Loader2 } from 'lucide-react';
import { api } from '@/api';
import { Logo } from './Logo';

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setStatus('loading');
    try {
      const res = await api.subscribeNewsletter(email);
      setStatus('success');
      setMessage(res.data.message);
      setEmail('');
      // Reset after 5 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Failed to subscribe. Please try again.');
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
    }
  };

  return (
    <footer className="bg-gray-900 border-t border-gray-800 mt-auto">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo & About */}
          <div className="col-span-1 md:col-span-2">
            <Logo className="h-16 w-auto mb-4" />
            <p className="text-gray-400 text-sm">
              The streaming home of the Women's Premier Soccer League. Live matches, scores,
              and highlights from across the United States.
            </p>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/scores" className="text-gray-400 hover:text-white transition-colors">Live Scores</Link></li>
              <li><Link to="/fixtures" className="text-gray-400 hover:text-white transition-colors">Fixtures</Link></li>
              <li><Link to="/partners" className="text-gray-400 hover:text-white transition-colors">Production Partners</Link></li>
            </ul>
          </div>
          
          {/* Contact & Newsletter */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>info@wpsl.tv</li>
            </ul>
            
            {/* Newsletter Subscription */}
            <div className="mt-6">
              <h4 className="text-white font-semibold mb-3">Subscribe to our Newsletter</h4>
              <form onSubmit={handleSubscribe} className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-sky-500"
                    disabled={status === 'loading' || status === 'success'}
                    required
                  />
                  <button
                    type="submit"
                    disabled={status === 'loading' || status === 'success'}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                      status === 'success' 
                        ? 'bg-green-500 text-white' 
                        : 'bg-sky-500 hover:bg-sky-600 text-white'
                    }`}
                  >
                    {status === 'loading' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : status === 'success' ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Mail className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {message && (
                  <p className={`text-xs ${status === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                    {message}
                  </p>
                )}
              </form>
            </div>
            
            <div className="mt-4">
              <Link to="/admin" className="text-gray-500 hover:text-gray-400 text-xs transition-colors">
                Admin Login
              </Link>
            </div>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-8 pt-6 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} WPSL TV. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

export default Footer;
